import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { NotificacionDto } from './dto/notificacion.dto';
import { NotificacionAsuntoDto } from './dto/notificacion-asunto.dto';

export interface NotificacionHistorialDto {
  destinatarios: {
    _id: string;
    email: string;
    nombrePersona: string;
    apellidoPersona: string;
    fullName: string;
  }[];
  adjuntos: string[];
  enviado: boolean;
  fechaEnvio: string;
  masivo: boolean;
  _id: string;
  asunto: string;
  cuerpo: string;
  createdAt: string;
  updatedAt: string;
}

const URL_BACKEND = environment.urlBackend;

@Injectable({
  providedIn: 'root'
})
export class NotificacionesService {

  constructor(
    private httpClient: HttpClient,
    private authService: AuthService
  ) { }

  private async getAuthHeaders(): Promise<HttpHeaders> {
    const jwt = await this.authService.loadJwToken();
    return new HttpHeaders({
      'Authorization': `Bearer ${jwt || ''}`,
      'x-token': jwt || '',
    });
  }

  createNotificacion(notificacion: NotificacionDto): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.getAuthHeaders();
        const { fechaEnvio, ...rest } = notificacion;

        const payload: NotificacionDto = {
          ...rest,
          destinatarios: Array.isArray(notificacion.destinatarios)
            ? notificacion.destinatarios
            : [notificacion.destinatarios]
        };

        const headersWithContent = headers.set('Content-Type', 'application/json');

        this.httpClient.post<any>(`${URL_BACKEND}/notificaciones`, payload, { headers: headersWithContent })
          .subscribe(resp => {
            const id = resp?.response?._id ?? resp?._id ?? resp;
            resolve(id);
          }, (error: HttpErrorResponse) => {
            console.error('Error creando notificación:', error);
            reject(error);
          });
      } catch (err) {
        reject(err);
      }
    });
  }

  sendNotificacion(id: string): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.getAuthHeaders();
        this.httpClient.post<any>(`${URL_BACKEND}/notificaciones/enviar/${id}`, {}, { headers })
          .subscribe(() => {
            resolve(true);
          }, error => {
            console.error('Error enviando notificación:', error);
            reject(false);
          });
      } catch (err) {
        reject(err);
      }
    });
  }

  getAsuntosActivos(): Promise<NotificacionAsuntoDto[]> {
    return new Promise(async resolve => {
      try {
        const headers = await this.getAuthHeaders();
        this.httpClient.get<any>(`${URL_BACKEND}/notificacion-asuntos/activos`, { headers })
          .subscribe(resp => {
            const list = resp?.response ?? resp ?? [];
            resolve(list);
          }, () => resolve([]));
      } catch {
        resolve([]);
      }
    });
  }

  addAsunto(asunto: NotificacionAsuntoDto): Promise<NotificacionAsuntoDto> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.getAuthHeaders();
        this.httpClient.post<any>(`${URL_BACKEND}/notificacion-asuntos`, asunto, { headers })
          .subscribe(resp => {
            resolve(resp?.response ?? resp);
          }, error => {
            console.error('Error agregando asunto:', error);
            reject(error);
          });
      } catch (err) {
        reject(err);
      }
    });
  }

  getMisMensajes(): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.getAuthHeaders();
        this.httpClient.get<any>(`${URL_BACKEND}/notificaciones/mis-mensajes`, { headers })
          .subscribe(resp => {
            const list = resp?.response ?? resp ?? [];
            resolve(list);
          }, error => reject(error));
      } catch (e) {
        reject(e);
      }
    });
  }

  getNoLeidosCount(): Promise<number> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.getAuthHeaders();
        this.httpClient.get<any>(`${URL_BACKEND}/notificaciones/mis-mensajes/no-leidos/count`, { headers })
          .subscribe(resp => {
            const val = resp?.response?.count ?? resp?.count ?? 0;
            resolve(val);
          }, error => reject(error));
      } catch (e) {
        reject(e);
      }
    });
  }

  marcarComoLeidaSegura(notificacionId: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.getAuthHeaders();
        this.httpClient.put<any>(`${URL_BACKEND}/notificaciones/mis-mensajes/${notificacionId}/leida`, {}, { headers })
          .subscribe(() => resolve(), error => reject(error));
      } catch (e) {
        reject(e);
      }
    });
  }

  getDestinatariosDetalle(id: string): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.getAuthHeaders();
        this.httpClient.get<any>(`${URL_BACKEND}/notificaciones/destinatarios-detalle/${id}`, { headers })
          .subscribe(resp => {
            const list = resp?.response ?? resp ?? [];
            resolve(list);
          }, error => reject(error));
      } catch (e) {
        reject(e);
      }
    });
  }

  getDetalleMetricas(id: string): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.getAuthHeaders();
        this.httpClient.get<any>(`${URL_BACKEND}/notificaciones/detalle-metricas/${id}`, { headers })
          .subscribe(resp => {
            const detail = resp?.response ?? resp ?? {};
            resolve(detail);
          }, error => reject(error));
      } catch (e) {
        reject(e);
      }
    });
  }

  deleteAsunto(asuntoId: string): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.getAuthHeaders();
        this.httpClient.delete<any>(`${URL_BACKEND}/notificacion-asuntos/${asuntoId}`, { headers })
          .subscribe(() => {
            resolve(true);
          }, error => {
            console.error('Error eliminando asunto:', error);
            reject(false);
          });
      } catch (err) {
        reject(err);
      }
    });
  }

  getHistorial(desde?: Date, hasta?: Date): Promise<NotificacionHistorialDto[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers: HttpHeaders = await this.getAuthHeaders();
        let url = `${URL_BACKEND}/notificaciones/historial`;
        const params: string[] = [];
        if (desde) {
          params.push(`desde=${desde.toISOString()}`);
        }
        if (hasta) {
          params.push(`hasta=${hasta.toISOString()}`);
        }
        if (params.length > 0) {
          url += '?' + params.join('&');
        }
        this.httpClient.get<any>(url, { headers }).subscribe(
          resp => {
            let resultado: NotificacionHistorialDto[] = resp?.response ?? resp ?? [];
            if (desde) {
              resultado = resultado.filter(item => new Date(item.fechaEnvio) >= desde);
            }
            if (hasta) {
              resultado = resultado.filter(item => new Date(item.fechaEnvio) <= hasta);
            }
            resolve(resultado);
          },
          error => reject(error)
        );
      } catch (e) {
        reject(e);
      }
    });
  }

}
